import React, { useMemo, useRef, useState } from 'react';
import { formatTimelineWindow } from './helpers';
import { applyBoundaryDraftSegments, getSourceSegments, getTimelineSegmentLabelMode } from './utils';
import type { TimelineLabelMode } from './utils';
import type { Segment, TimelineLanePlacement } from './types';
import {
  timelineInfoRowStyle,
  timelineTimeWindowStyle,
  getTimelineTickStyle,
  getSourceSegmentTimelineStyle,
  getTimelineLaneLabelStyle,
  getTimelineProgressClassStyle,
  getBoundaryHandleStyle,
  getTimelinePlayheadClassStyle
} from './styles';
import TimelineLanePlacementHandle from './TimelineLanePlacementHandle';

interface BoundaryTimelineEditorProps {
  duration: number;
  currentTime: number;
  segments: Segment[];
  activeSegment: string | null;
  onSeek: (time: number) => void;
  onSegmentClick: (segmentId: string) => void;
  onSegmentsChange: (segments: Segment[]) => void;
  timelineColor?: string;
  showTimeWindow?: boolean;
  zoomLevel?: number;
  lanePlacement?: TimelineLanePlacement;
  onLanePlacementChange?: (placement: TimelineLanePlacement) => void;
}

const MIN_SEGMENT_DURATION = 0.1;
const COLLAPSE_SNAP_SECONDS = MIN_SEGMENT_DURATION / 2;

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const roundToHundredths = (value: number) => {
  return Math.round(value * 100) / 100;
};

const sortSourceSegments = (segments: Segment[]) => {
  return [...segments].sort((a, b) => a.startTime - b.startTime);
};

const BoundaryTimelineEditor: React.FC<BoundaryTimelineEditorProps> = ({
  duration,
  currentTime,
  segments,
  activeSegment,
  onSeek,
  onSegmentClick,
  onSegmentsChange,
  timelineColor = 'rgba(100, 116, 139, 0.48)',
  showTimeWindow = true,
  zoomLevel = 1,
  lanePlacement = 'under-video',
  onLanePlacementChange
}) => {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [draggingBoundaryIndex, setDraggingBoundaryIndex] = useState<number | null>(null);
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const markerCount = 5;
  const timelineZoomLevel = Math.max(1, Number.isFinite(zoomLevel) ? zoomLevel : 1);
  const sourceSegments = useMemo(
    () => sortSourceSegments(getSourceSegments(segments)),
    [segments]
  );

  const seekFromClientX = (clientX: number) => {
    if (!timelineRef.current || duration <= 0) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = clamp((clientX - rect.left) / rect.width, 0, 1);
    onSeek(position * duration);
  };

  const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingBoundaryIndex !== null || isDraggingSeek) return;
    seekFromClientX(event.clientX);
  };

  const handleSeekPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (duration <= 0) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDraggingSeek(true);
    seekFromClientX(event.clientX);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      seekFromClientX(moveEvent.clientX);
    };

    const handlePointerUp = () => {
      setIsDraggingSeek(false);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  };

  const handleSeekKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (duration <= 0) return;

    const step = event.shiftKey ? 5 : 1;
    let nextTime: number | null = null;

    if (event.key === 'ArrowLeft') {
      nextTime = currentTime - step;
    } else if (event.key === 'ArrowRight') {
      nextTime = currentTime + step;
    } else if (event.key === 'Home') {
      nextTime = 0;
    } else if (event.key === 'End') {
      nextTime = duration;
    }

    if (nextTime === null) return;

    event.preventDefault();
    event.stopPropagation();
    onSeek(clamp(nextTime, 0, duration));
  };

  const publishBoundaryChange = (updatedSourceSegments: Segment[], seekTime: number) => {
    const repairedSegments = applyBoundaryDraftSegments(
      segments,
      sortSourceSegments(updatedSourceSegments)
    );

    onSegmentsChange(repairedSegments);
    onSeek(seekTime);
  };

  const updateBoundary = (boundaryIndex: number, clientX: number) => {
    if (!timelineRef.current || duration <= 0) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const position = clamp((clientX - rect.left) / rect.width, 0, 1);
    const targetTime = roundToHundredths(position * duration);
    const isFinalBoundary = boundaryIndex === sourceSegments.length - 1;

    if (isFinalBoundary) {
      const finalSegment = sourceSegments[boundaryIndex];
      if (!finalSegment || duration < finalSegment.startTime) return;

      const minBoundaryTime = Math.min(
        finalSegment.startTime + MIN_SEGMENT_DURATION,
        duration
      );
      const snappedBoundary = clamp(targetTime, minBoundaryTime, duration);
      const updatedSegments = sourceSegments.map((segment, index) => (
        index === boundaryIndex
          ? { ...segment, endTime: snappedBoundary }
          : segment
      ));

      publishBoundaryChange(updatedSegments, snappedBoundary);
      return;
    }

    const leftSegment = sourceSegments[boundaryIndex];
    const rightSegment = sourceSegments[boundaryIndex + 1];
    if (!leftSegment || !rightSegment) return;

    const previousBoundaryTime = leftSegment.startTime;
    const nextBoundaryTime = rightSegment.endTime;
    const boundaryTime = clamp(targetTime, previousBoundaryTime, nextBoundaryTime);

    if (boundaryTime <= previousBoundaryTime + COLLAPSE_SNAP_SECONDS) {
      const updatedSegments = sourceSegments.flatMap((segment, index) => {
        if (index === boundaryIndex) return [];
        if (index === boundaryIndex + 1) {
          return [{ ...segment, startTime: previousBoundaryTime }];
        }
        return [segment];
      });

      publishBoundaryChange(updatedSegments, previousBoundaryTime);
      return;
    }

    if (boundaryTime >= nextBoundaryTime - COLLAPSE_SNAP_SECONDS) {
      const updatedSegments = sourceSegments.flatMap((segment, index) => {
        if (index === boundaryIndex + 1) return [];
        if (index === boundaryIndex) {
          return [{ ...segment, endTime: nextBoundaryTime }];
        }
        return [segment];
      });

      publishBoundaryChange(updatedSegments, nextBoundaryTime);
      return;
    }

    const minBoundaryTime = leftSegment.startTime + MIN_SEGMENT_DURATION;
    const maxBoundaryTime = rightSegment.endTime - MIN_SEGMENT_DURATION;
    if (maxBoundaryTime < minBoundaryTime) return;

    const snappedBoundary = clamp(boundaryTime, minBoundaryTime, maxBoundaryTime);

    const updatedSegments = sourceSegments.map((segment, index) => {
      if (index === boundaryIndex) {
        return { ...segment, endTime: snappedBoundary };
      }
      if (index === boundaryIndex + 1) {
        return { ...segment, startTime: snappedBoundary };
      }
      return segment;
    });

    publishBoundaryChange(updatedSegments, snappedBoundary);
  };

  const handleBoundaryMouseDown = (boundaryIndex: number, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    setDraggingBoundaryIndex(boundaryIndex);
    updateBoundary(boundaryIndex, event.clientX);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      updateBoundary(boundaryIndex, moveEvent.clientX);
    };

    const handleMouseUp = () => {
      setDraggingBoundaryIndex(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleBoundaryTouchStart = (boundaryIndex: number, event: React.TouchEvent<HTMLButtonElement>) => {
    if (event.touches.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    setDraggingBoundaryIndex(boundaryIndex);
    updateBoundary(boundaryIndex, event.touches[0].clientX);

    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (moveEvent.touches.length === 0) return;
      moveEvent.preventDefault();
      updateBoundary(boundaryIndex, moveEvent.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      setDraggingBoundaryIndex(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchEnd);
  };

  const getSegmentLabelText = (
    labelMode: TimelineLabelMode,
    segment: Segment,
    index: number
  ) => {
    if (labelMode === 'number') return String(index + 1);
    if (labelMode === 'name') return segment.name;
    return '';
  };

  return (
    <div
      className={`timeline-shell boundary-timeline-shell timeline-lane-placement-${lanePlacement}`}
      data-testid="boundary-timeline-shell"
      data-lane-placement={lanePlacement}
    >
      <div
        className={`timeline-zoom-scroll ${timelineZoomLevel > 1 ? 'is-zoomed' : ''} boundary-timeline-zoom-scroll`}
        data-testid="boundary-timeline-zoom-scroll"
      >
        <div
          ref={timelineRef}
          className={`timeline-inner-track boundary-timeline-inner timeline-lane-placement-${lanePlacement} ${isDraggingSeek ? 'is-seeking' : ''}`}
          data-testid="boundary-timeline-root"
          data-lane-placement={lanePlacement}
          style={{
            width: `${timelineZoomLevel * 100}%`,
            minWidth: '100%'
          }}
          onClick={handleTimelineClick}
        >
          <div
            className="timeline-lanes boundary-timeline-lanes"
            data-testid="boundary-timeline-lanes"
          >
            <div
              className="timeline-lane-stack boundary-timeline-lane-stack"
              data-testid="boundary-timeline-lane-stack"
            >
              <TimelineLanePlacementHandle
                placement={lanePlacement}
                onPlacementChange={onLanePlacementChange}
              />
              <div
                className="timeline-track-stack boundary-timeline-track-stack"
                data-testid="boundary-timeline-segments-layer"
              >
                <div
                  className="timeline-source-lane timeline-source-track is-single-lane boundary-source-lane"
                  data-testid="boundary-timeline-source-lane"
                >
                  {sourceSegments.map((segment, index) => {
                    const isActive = activeSegment === segment.id;
                    const labelMode = getTimelineSegmentLabelMode(segment, duration, {
                      kind: 'source',
                      zoomLevel: timelineZoomLevel,
                      isActive
                    });
                    const labelText = getSegmentLabelText(labelMode, segment, index);
                    const segmentClassName = [
                      'timeline-source-segment',
                      'boundary-source-segment',
                      isActive ? 'is-active' : '',
                      segment.isDisabled ? 'is-disabled' : ''
                    ].filter(Boolean).join(' ');

                    return (
                      <button
                        type="button"
                        key={segment.id}
                        className={segmentClassName}
                        data-testid={`boundary-timeline-segment-${segment.id}`}
                        data-label-mode={labelMode}
                        aria-label={segment.name}
                        aria-disabled={segment.isDisabled ? 'true' : 'false'}
                        disabled={Boolean(segment.isDisabled)}
                        style={{
                          ...getSourceSegmentTimelineStyle(segment, duration, isActive, false),
                          zIndex: isActive ? 9 : 6
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (segment.isDisabled) return;
                          onSegmentClick(segment.id);
                        }}
                      >
                        {labelMode !== 'none' && (
                          <span
                            className={`timeline-lane-label timeline-source-label ${isActive ? 'is-active' : ''} ${segment.isDisabled ? 'is-disabled' : ''}`}
                            data-testid={`boundary-timeline-label-${segment.id}`}
                            data-label-mode={labelMode}
                            style={getTimelineLaneLabelStyle(isActive, Boolean(segment.isDisabled))}
                          >
                            {labelText}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {sourceSegments.map((segment, index) => {
                    const isFinalBoundary = index === sourceSegments.length - 1;
                    const isDragging = draggingBoundaryIndex === index;
                    const boundaryClassName = [
                      'timeline-boundary-handle',
                      isFinalBoundary ? 'is-final' : 'is-internal',
                      isDragging ? 'is-dragging' : ''
                    ].filter(Boolean).join(' ');

                    return (
                      <button
                        type="button"
                        key={`boundary-${segment.id}`}
                        className={boundaryClassName}
                        data-testid={`boundary-timeline-boundary-handle-${index}`}
                        data-boundary-kind={isFinalBoundary ? 'final' : 'internal'}
                        onMouseDown={(event) => handleBoundaryMouseDown(index, event)}
                        onTouchStart={(event) => handleBoundaryTouchStart(index, event)}
                        onClick={(event) => event.stopPropagation()}
                        style={getBoundaryHandleStyle(segment.endTime, duration, isDragging, segment.color || timelineColor)}
                        title="Drag boundary left/right"
                        aria-label={isFinalBoundary ? 'Drag final segment end boundary' : 'Drag segment boundary'}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div
            className="boundary-timeline-transport-layer"
            data-testid="boundary-timeline-transport-layer"
          >
            <div className="timeline-background">
              <div
                className="timeline-progress"
                style={getTimelineProgressClassStyle(currentTime, duration, timelineColor)}
              />
              <div className="timeline-tick-layer">
                {[...Array(markerCount)].map((_, i) => (
                  <span
                    key={`tick-${i}`}
                    className={`timeline-tick ${i === 0 || i === markerCount - 1 ? 'is-edge' : ''}`}
                    style={getTimelineTickStyle(i, markerCount)}
                  />
                ))}
              </div>
            </div>

            <div
              role="slider"
              tabIndex={0}
              className={`timeline-seek-hit-area ${isDraggingSeek ? 'is-dragging' : ''}`}
              data-testid="boundary-timeline-seek-hit-area"
              aria-label="Playback position"
              aria-valuemin={0}
              aria-valuemax={Number(duration.toFixed(1))}
              aria-valuenow={Number(clamp(currentTime, 0, duration).toFixed(1))}
              title="Drag playback position"
              onPointerDown={handleSeekPointerDown}
              onKeyDown={handleSeekKeyDown}
              onClick={(event) => event.stopPropagation()}
            />

            <div
              aria-hidden="true"
              className={`timeline-playhead boundary-timeline-playhead ${isDraggingSeek ? 'is-dragging' : ''}`}
              style={getTimelinePlayheadClassStyle(currentTime, duration, timelineColor)}
            >
              <div className="timeline-playhead-knob" />
            </div>
          </div>
        </div>
      </div>

      {showTimeWindow && (
        <div style={timelineInfoRowStyle}>
          <span style={timelineTimeWindowStyle}>
            {formatTimelineWindow(currentTime, duration)}
          </span>
        </div>
      )}
    </div>
  );
};

export default BoundaryTimelineEditor;
